import { FormControlLabel, InputAdornment, TextField } from '@mui/material';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { API_MAP, VTCLOUD_MARKETPLACE } from '../../../../../modules/API/constants_ts';
import axiosClient from '../../../api/axiosClient';
import { hideDialog } from '../../../base/dialog/actions';
import { IconClear } from '../../../base/icons/svg';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import Dialog from '../../../base/ui/components/web/Dialog';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification, showWarningNotification } from '../../../notifications';
import { USER_FUNCTIONS } from '../../../user/constant';
import { getFunctionValue } from '../../../user/functions';
import { getLimitConnections, getTmpSession, getUsedConnections } from '../../functions';
import { createAndStartRoomCascadeSession, setLimitConnections, setTmpSession, setUsedConnections } from '../../slice';
import { CR_NOTIFICATIONS } from '../../constants';
const useStyles = makeStyles()(theme => {
    return {
        rcDialog: {
            width: 'auto'
        },
        rcField: {
            marginTop: '14px',
        },
        rcErrText: {
            fontSize: '14px',
            color: theme.palette.errorHelpText
        },
        rcContainer: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '12px'
        }
    };
});
const queryClient = new QueryClient();
export default function DialogRoomCascade({ keepData = false }) {
    return (React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(RoomCascade, { keepData: keepData })));
}
function RoomCascade({ keepData = false }) {
    const { cx, classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const defaultSession = { roomId: '', password: '', nearSiteName: '', farSiteName: '' };
    const { handleSubmit, control, formState: { errors, isValid }, setValue } = useForm({ mode: "onChange" });
    const limitConnectionFunction = useSelector((state) => getFunctionValue(state, USER_FUNCTIONS.CASCADE_CONNECTION_LIMIT, 0));
    const limitConnection = useSelector(getLimitConnections);
    const usedConnection = useSelector(getUsedConnections);
    const tmpSession = useSelector(getTmpSession);
    const goToMarket = () => window.open(VTCLOUD_MARKETPLACE);
    useEffect(() => {
        if (keepData) {
            setValue("roomId", tmpSession?.roomId || defaultSession.roomId);
            setValue("password", tmpSession?.password || defaultSession.password || '');
            setValue("nearSiteName", tmpSession?.nearSiteName || defaultSession.nearSiteName || '');
            setValue("farSiteName", tmpSession?.farSiteName || defaultSession.farSiteName || '');
        }
        // return () => {
        //     dispatch(setTmpSession(session))
        // }
    }, []);
    const { status, data, error } = useQuery({
        queryKey: ['check-limit'],
        queryFn: async () => {
            const response = await axiosClient.get(API_MAP.CASCADE.CHECK_LIMIT);
            const { cascade_connection_limit: limitConnectionQuery, total_used_cascade_connection: usedConnectionQuery } = response || { cascade_connection_limit: limitConnectionFunction };
            if (usedConnectionQuery >= limitConnectionQuery) {
                dispatch(hideDialog());
                dispatch(showWarningNotification({
                    titleKey: `roomCascade.notification.warning.cascade-connection-limit-reached`,
                    description: React.createElement("span", null,
                        t('roomCascade.notification.warning.cascade-connection-limit-reached-content'),
                        " ",
                        React.createElement("span", { onClick: goToMarket, style: { color: "#EE0033", textDecoration: 'underline' } }, t('roomCascade.notification.warning.license')),
                        t('roomCascade.notification.warning.cascade-connection-limit-reached-content-1')),
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            dispatch(setLimitConnections(limitConnectionQuery));
            dispatch(setUsedConnections(usedConnectionQuery));
            return response;
        },
    });
    if (status === 'pending')
        return React.createElement("h1", null, "Loading...");
    if (status === 'error')
        return React.createElement("span", null,
            "Error: ",
            error.message);
    const onSubmit = async (data) => {
        dispatch(setTmpSession(data));
        dispatch(createAndStartRoomCascadeSession(data));
        dispatch(showNotification({
            uid: CR_NOTIFICATIONS.CASCADE_WRONG_PASS_ID,
            titleKey: `roomCascade.notification.success.sended`,
            description: i18next.t(`roomCascade.notification.success.sended`),
            disableClosing: false,
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        dispatch(hideDialog());
    };
    return (React.createElement("form", { onSubmit: (e) => {
            e.preventDefault();
        } },
        React.createElement(Dialog, { titleKey: 'roomCascade.form.title', ok: {
                translationKey: 'roomCascade.form.connect',
                disabled: usedConnection >= limitConnection || !isValid
            }, className: cx(classes.rcDialog), onSubmit: handleSubmit(onSubmit), disableEnter: usedConnection >= limitConnection || !isValid },
            React.createElement("div", { className: cx(classes.rcContainer) },
                React.createElement(Controller, { name: 'roomId', control: control, defaultValue: '', rules: {
                        required: 'roomCascade.form.destRoomHelper',
                        validate: (value) => /^\d{10}$/.test(value) || 'roomCascade.form.destRoomHelperValid'
                    }, render: ({ field }) => (React.createElement(FormControlLabel, { label: t('roomCascade.form.destRoom'), required: true, labelPlacement: 'top', control: React.createElement(TextField, { ...field, autoFocus: true, fullWidth: true, id: 'destRoomId', placeholder: t('roomCascade.form.destRoomPlaceholder'), error: !!errors.roomId?.message, helperText: errors.roomId?.message && t(errors.roomId?.message), InputProps: {
                                endAdornment: (React.createElement(InputAdornment, { position: 'end' }, field.value && React.createElement(Tooltip, { content: t('textField.clear') },
                                    React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setValue('roomId', '', { shouldValidate: true }), className: 'icon-input' }))))
                            } }) })) }),
                React.createElement(Controller, { name: 'password', control: control, defaultValue: '', rules: { maxLength: { value: 255, message: 'roomCascade.form.destPasswordMaxLength' } }, render: ({ field }) => (React.createElement(FormControlLabel, { label: t('roomCascade.form.destPassword'), labelPlacement: 'top', control: React.createElement(TextField, { ...field, fullWidth: true, id: 'password', placeholder: t('roomCascade.form.destPasswordPlaceholder'), error: !!errors.password?.message, helperText: errors.password?.message, InputProps: {
                                endAdornment: (React.createElement(InputAdornment, { position: 'end' }, field.value && React.createElement(Tooltip, { content: t('textField.clear') },
                                    React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setValue('password', ''), className: 'icon-input' }))))
                            } }) })) }),
                React.createElement(Controller, { name: 'farSiteName', control: control, defaultValue: '', rules: { required: 'roomCascade.form.destNameHelper', maxLength: { value: 64, message: 'roomCascade.form.destNameHelperValid' } }, render: ({ field }) => (React.createElement(FormControlLabel, { label: t('roomCascade.form.destName'), required: true, labelPlacement: 'top', control: React.createElement(TextField, { ...field, fullWidth: true, id: 'destName', placeholder: t('roomCascade.form.destNamePlaceholder'), error: !!errors.farSiteName?.message, helperText: errors.farSiteName?.message && t(errors.farSiteName?.message), InputProps: {
                                endAdornment: (React.createElement(InputAdornment, { position: 'end' }, field.value && React.createElement(Tooltip, { content: t('textField.clear') },
                                    React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setValue('farSiteName', '', { shouldValidate: true }), className: 'icon-input' }))))
                            } }) })) }),
                React.createElement(Controller, { name: 'nearSiteName', control: control, defaultValue: '', rules: { required: 'roomCascade.form.srcNameHelper', maxLength: { value: 64, message: 'roomCascade.form.srcNameHelperValid' } }, render: ({ field }) => (React.createElement(FormControlLabel, { label: t('roomCascade.form.srcName'), required: true, labelPlacement: 'top', control: React.createElement(TextField, { ...field, fullWidth: true, id: 'srcName', placeholder: t('roomCascade.form.srcNamePlaceholder'), error: !!errors.nearSiteName?.message, helperText: errors.nearSiteName?.message && t(errors.nearSiteName?.message), InputProps: {
                                endAdornment: (React.createElement(InputAdornment, { position: 'end' }, field.value && React.createElement(Tooltip, { content: t('textField.clear') },
                                    React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setValue('nearSiteName', '', { shouldValidate: true }), className: 'icon-input' }))))
                            } }) })) }),
                React.createElement("span", { className: cx(classes.rcField) },
                    t('roomCascade.form.connectionNumber'),
                    React.createElement("span", { style: { fontWeight: 'bold' } }, ` ${Math.max(limitConnection - usedConnection, 0)}/${limitConnection}`))))));
}
